import { useCallback, useMemo, useState } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import useQueryString from "helpers/useQueryString";
import { DEFAULT_PAGER, InfiniteListContext, InfiniteListPager } from "context/InfiniteList/Context";

type ListData<T> = Array<T> & { pager: InfiniteListPager; active_filters: Record<string, unknown> };

export type FetcherParams = Record<string, unknown>;
export type ListDataFetcher = (params?: FetcherParams) => Promise<ListData<unknown>>;

type InfiniteListProviderProps = {
    dataFetcher: ListDataFetcher;
    defaultFetcherParams: FetcherParams;
    cacheKey?: string;
    children: React.ReactNode;
};

/**
 * Data provider for infinite-scroll list pages.
 * Provides InfiniteListContext — consumed by useInfiniteList, InfiniteListArea,
 * and any component that receives explicit override props (FilterForm, SelectSort).
 */
function InfiniteListProvider<T>({ dataFetcher, defaultFetcherParams, cacheKey, children }: InfiniteListProviderProps) {
    const queryString = useQueryString();

    const [fetcherParams, setFetcherParams] = useState<FetcherParams>(() => ({
        ...defaultFetcherParams,
        ...(queryString.values ?? {}),
    }));

    const { data, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage, isError, refetch } = useInfiniteQuery({
        queryKey: ["infinite-list", cacheKey, fetcherParams],
        queryFn: ({ pageParam }) => dataFetcher({ ...fetcherParams, page: pageParam }) as Promise<ListData<T>>,
        initialPageParam: 1,
        getNextPageParam: (lastPage: ListData<T>) => {
            const { current_page, last_page } = lastPage.pager;
            return current_page < last_page ? current_page + 1 : undefined;
        },
        refetchOnMount: "always",
    });

    const lastPage = data?.pages[data.pages.length - 1];

    const allItems = useMemo<T[]>(() => data?.pages.flatMap((page) => [...page] as T[]) ?? [], [data]);

    const changeFetcherParams = useCallback((params?: FetcherParams) => {
        setFetcherParams(params ?? {});
    }, []);

    const refreshData = useCallback(() => void refetch(), [refetch]);

    const requestedPerPage = Number(fetcherParams.per_page ?? DEFAULT_PAGER.per_page);

    const contextValue = useMemo(
        () => ({
            items: allItems as unknown[],
            pager: lastPage?.pager ?? DEFAULT_PAGER,
            requestedPerPage,
            activeFilters: (fetcherParams ?? {}) as Record<string, unknown>,
            defaultFetcherParams: defaultFetcherParams,
            isLoading,
            isFetchingNextPage: !!isFetchingNextPage,
            hasNextPage: !!hasNextPage,
            hasError: isError,
            fetchNextPage,
            changeFetcherParams,
            refreshData,
        }),
        [
            allItems,
            lastPage,
            requestedPerPage,
            fetcherParams,
            defaultFetcherParams,
            isLoading,
            isFetchingNextPage,
            hasNextPage,
            isError,
            fetchNextPage,
            changeFetcherParams,
            refreshData,
        ],
    );

    return <InfiniteListContext.Provider value={contextValue}>{children}</InfiniteListContext.Provider>;
}

export default InfiniteListProvider;
